var _a;
import moment from 'moment';
import { SuperintendentVisitType } from '@common/enums/superintendentBooking';
export var CONTACT_PHONE = '(555) 010-0199';
export var CONTACT_PHONE_HREF = 'tel:+15550100199';
export var BRAND_NAME = 'Invitation Homes';
var LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
var VISIT_COPY = (_a = {},
    _a[SuperintendentVisitType.MoveInOrientation] = {
        name: 'move-in orientation',
        label: 'Move-in orientation',
        anchorDate: 'move-in date',
        intro: 'A superintendent will meet you at your new home and walk you through it.'
    },
    _a[SuperintendentVisitType.MoveOutConsultation] = {
        name: 'move-out consultation',
        label: 'Move-out consultation',
        anchorDate: 'move-out date',
        intro: 'A superintendent will meet you at your home to go over your move-out.',
        isArrivalWindow: true,
        beforeVisit: [
            'Please keep pets secured.',
            'An adult 18 or older needs to be home.',
            'A missed appointment may lead to a charge.'
        ]
    },
    _a);
export var ARRIVAL_WINDOW_NOTE = 'Your superintendent will arrive any time in this window.';
var DEFAULT_COPY = {
    name: 'visit',
    label: 'Visit',
    anchorDate: 'visit date',
    intro: 'A superintendent will meet you at your home.'
};
export var getVisitCopy = function (visitTypeId) { return VISIT_COPY[visitTypeId] || DEFAULT_COPY; };
export var parseLocal = function (value) { return moment(value, LOCAL_FORMAT); };
export var formatShortDate = function (value) { return parseLocal(value).format('ddd, MMM D'); };
export var formatLongDate = function (value) { return parseLocal(value).format('dddd, MMMM D'); };
export var formatFullDate = function (value) { return parseLocal(value).format('dddd, MMMM D, YYYY'); };
export var formatClock = function (value) { return parseLocal(value).format('h:mm A'); };
export var formatDayHeading = function (date) { return moment(date, 'YYYY-MM-DD').format('dddd, MMMM D'); };
export var formatCutoff = function (date) { return moment(date, 'YYYY-MM-DD').format('ddd, MMM D'); };
export var formatTimeRange = function (slot, withZone) {
    if (withZone === void 0) { withZone = false; }
    var start = parseLocal(slot.startLocal);
    var end = parseLocal(slot.endLocal);
    var sameMeridiem = start.format('A') === end.format('A');
    var range = start.format(sameMeridiem ? 'h:mm' : 'h:mm A') + " \u2013 " + end.format('h:mm A');
    return withZone && slot.timeZone ? range + " " + slot.timeZone : range;
};
// "8 AM – 12 PM": drops ":00" so long arrival windows stay short.
export var formatCompactRange = function (slot) {
    var clock = function (value) {
        var time = parseLocal(value);
        return time.format(time.minutes() === 0 ? 'h A' : 'h:mm A');
    };
    return clock(slot.startLocal) + " \u2013 " + clock(slot.endLocal);
};
export var formatArrivalWindow = function (slot, withZone) {
    if (withZone === void 0) { withZone = false; }
    var range = "Arrival window " + formatCompactRange(slot);
    return withZone && slot.timeZone ? range + " " + slot.timeZone : range;
};
export var formatClosedDate = function (date) { return moment(date, 'YYYY-MM-DD').format('MMM D, YYYY'); };
// The end date is the last day that can still be booked, so both ends count (Nov 1 → Nov 14 is a 14-day window).
export var getWindowDays = function (start, end) {
    if (!start || !end) {
        return 0;
    }
    var days = moment(end, 'YYYY-MM-DD').diff(moment(start, 'YYYY-MM-DD'), 'days') + 1;
    return days > 0 ? days : 0;
};
export var formatDuration = function (slot) {
    var minutes = parseLocal(slot.endLocal).diff(parseLocal(slot.startLocal), 'minutes');
    if (!(minutes > 0)) {
        return '';
    }
    if (minutes % 60 === 0) {
        var hours = minutes / 60;
        return "About " + hours + " hour" + (hours > 1 ? 's' : '');
    }
    return "About " + minutes + " minutes";
};
export var formatDurationPhrase = function (slot) { return formatDuration(slot).replace(/^About /, ''); };
export var formatStreet = function (location) {
    return location ? [location.addressLine1, location.addressLine2].filter(Boolean).join(', ') : '';
};
export var formatCityState = function (location) {
    return location ? [location.city, location.state].filter(Boolean).join(', ') : '';
};
export var formatAddress = function (location) {
    return [formatStreet(location), formatCityState(location)].filter(Boolean).join(', ');
};
var toCalendarStamp = function (local, offsetMinutes) {
    if (typeof offsetMinutes !== 'number') {
        return parseLocal(local).format('YYYYMMDDTHHmmss');
    }
    return moment.utc(local, LOCAL_FORMAT).subtract(offsetMinutes, 'minutes').format('YYYYMMDDTHHmmss[Z]');
};
var toIsoInstant = function (local, offsetMinutes) {
    if (typeof offsetMinutes !== 'number') {
        return parseLocal(local).format(LOCAL_FORMAT);
    }
    return moment.utc(local, LOCAL_FORMAT).subtract(offsetMinutes, 'minutes').format('YYYY-MM-DDTHH:mm:ss[Z]');
};
export var getGoogleCalendarUrl = function (_a) {
    var title = _a.title, slot = _a.slot, location = _a.location, details = _a.details;
    var dates = toCalendarStamp(slot.startLocal, slot.utcOffsetMinutes) + "/" + toCalendarStamp(slot.endLocal, slot.utcOffsetMinutes);
    return 'https://calendar.google.com/calendar/render?action=TEMPLATE'
        + ("&text=" + encodeURIComponent(title) + "&dates=" + dates)
        + ("&location=" + encodeURIComponent(location) + "&details=" + encodeURIComponent(details));
};
export var getOutlookCalendarUrl = function (_a) {
    var title = _a.title, slot = _a.slot, location = _a.location, details = _a.details;
    return 'https://outlook.live.com/calendar/0/deeplink/compose?path=%2Fcalendar%2Faction%2Fcompose&rru=addevent'
        + ("&subject=" + encodeURIComponent(title))
        + ("&startdt=" + encodeURIComponent(toIsoInstant(slot.startLocal, slot.utcOffsetMinutes)))
        + ("&enddt=" + encodeURIComponent(toIsoInstant(slot.endLocal, slot.utcOffsetMinutes)))
        + ("&location=" + encodeURIComponent(location) + "&body=" + encodeURIComponent(details));
};
var hashText = function (value) {
    var hash = 5381;
    for (var i = 0; i < value.length; i += 1) {
        hash = ((hash * 33) ^ value.charCodeAt(i)) >>> 0;
    }
    return hash.toString(36);
};
var getIcsUid = function (_a) {
    var title = _a.title, slot = _a.slot, location = _a.location;
    return toCalendarStamp(slot.startLocal, slot.utcOffsetMinutes) + "-" + hashText(title + "|" + location) + "@superintendent-booking";
};
var ICS_LINE_OCTETS = 75;
var escapeIcsText = function (value) { return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r\n|\r|\n/g, '\\n'); };
var utf8Length = function (char) {
    var code = char.charCodeAt(0);
    if (char.length > 1) {
        return 4;
    }
    if (code < 0x80) {
        return 1;
    }
    return code < 0x800 ? 2 : 3;
};
var foldIcsLine = function (line) {
    var parts = [];
    var current = '';
    var octets = 0;
    var i = 0;
    while (i < line.length) {
        var code = line.charCodeAt(i);
        var isSurrogatePair = code >= 0xd800 && code <= 0xdbff && i + 1 < line.length;
        var char = isSurrogatePair ? line.substr(i, 2) : line.charAt(i);
        var size = utf8Length(char);
        var limit = parts.length === 0 ? ICS_LINE_OCTETS : ICS_LINE_OCTETS - 1;
        if (octets + size > limit) {
            parts.push(current);
            current = '';
            octets = 0;
        }
        current += char;
        octets += size;
        i += char.length;
    }
    parts.push(current);
    return parts.join('\r\n ');
};
export var buildIcs = function (event) {
    var title = event.title, slot = event.slot, location = event.location, details = event.details;
    var lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Superintendent Booking//EN',
        'BEGIN:VEVENT',
        "UID:" + getIcsUid(event),
        "DTSTAMP:" + moment.utc().format('YYYYMMDDTHHmmss[Z]'),
        "DTSTART:" + toCalendarStamp(slot.startLocal, slot.utcOffsetMinutes),
        "DTEND:" + toCalendarStamp(slot.endLocal, slot.utcOffsetMinutes),
        "SUMMARY:" + escapeIcsText(title),
        "LOCATION:" + escapeIcsText(location),
        "DESCRIPTION:" + escapeIcsText(details),
        'END:VEVENT',
        'END:VCALENDAR'
    ];
    return lines.map(foldIcsLine).join('\r\n') + "\r\n";
};
export var downloadIcs = function (event) {
    try {
        var blob = new Blob([buildIcs(event)], { type: 'text/calendar;charset=utf-8' });
        var url_1 = URL.createObjectURL(blob);
        var link = document.createElement('a');
        link.href = url_1;
        link.download = 'appointment.ics';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(function () { return URL.revokeObjectURL(url_1); }, 1000);
    }
    catch (e) {
    }
};
